import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Container, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { SafeComponent } from '@fiverr-private/orca';
import { Video } from '@fiverr-private/media';
import { SearchBar } from '@fiverr-private/search_bar';
import { getContext } from '@fiverr-private/fiverr_context';
import { translate, I18n } from '@fiverr-private/i18n-react';
import { MUSTANGS_LOHP_LONG_TERM_HIRING_EXPERIMENT } from '../../utils/experiments/experimentsEnum';
import { getTestVariation } from '../../utils/experiments';
import { VARIANTS } from '../../utils/types/variants';
import { videoEventsHandler } from '../../utils/videoEventsHandler';
import { VIDEO_POSTERS, VIDEO_SOURCES } from '../../constants';
import VideoControlButton from '../VideoControlButton';
import PopularServices from '../PopularServices';
import TrustedBySection from '../TrustedBySection';
import { LongTermHiringVariants } from '../LongTermHiringVariants';

import styles from './styles.module.scss';

interface Props {
    shouldLoadVideo?: boolean;
    inExpandableSearchBar?: boolean;
}

const VideoHeroBanner = ({ shouldLoadVideo = true, inExpandableSearchBar = false }: Props) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

    const handleVideoButtonClick = () => {
        if (!videoRef.current) return;

        const params = { component_name: 'VideoHeroBanner' };

        if (!isPlaying) {
            videoRef.current.play();
            videoEventsHandler({ actionType: 'Play', params });
            setIsPlaying(true);
        } else {
            videoRef.current.pause();
            videoEventsHandler({ actionType: 'Pause', params });
            setIsPlaying(false);
        }
    };

    const { abTests } = getContext();
    const longTermHiringVariant = getTestVariation(MUSTANGS_LOHP_LONG_TERM_HIRING_EXPERIMENT, abTests);

    useEffect(() => {
        const mediaQuery = window.matchMedia && window?.matchMedia('(prefers-reduced-motion: reduce)');

        setPrefersReducedMotion(mediaQuery?.matches);

        const handleChange = () => setPrefersReducedMotion(mediaQuery?.matches);

        mediaQuery.addEventListener('change', handleChange);

        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    return (
        <Stack width="100%" position="relative" height={{ default: '572px', sm: '680px' }} marginBottom="7">
            <Container
                className="wrapper"
                width="100%"
                position="absolute"
                top="0px"
                right="0px"
                left="0px"
                bottom="0px"
            >
                {prefersReducedMotion && (
                    <Container
                        style={{ background: `url(${VIDEO_POSTERS.desktop}) top #000 no-repeat` }}
                        className={styles.placeholder}
                        aria-hidden
                    />
                )}
                {!prefersReducedMotion && (
                    <Video
                        width="100%"
                        height="100%"
                        autoPlay
                        playsInline
                        preload="auto"
                        poster={VIDEO_POSTERS.desktop}
                        muted
                        loop={true}
                        controls={false}
                        ref={videoRef}
                        sources={shouldLoadVideo ? [VIDEO_SOURCES.desktop] : []}
                        aria-hidden
                    />
                )}
            </Container>
            <Stack
                width="100%"
                height="100%"
                display="flex"
                direction="column"
                alignItems="flexStart"
                zIndex="2"
                margin="auto"
                maxWidth="1400px"
                className={classNames(styles.container, {
                    [styles['lohp-test']]: longTermHiringVariant !== VARIANTS.A,
                })}
            >
                <Stack
                    height="100%"
                    display="flex"
                    direction="column"
                    alignItems="flexStart"
                    maxWidth={{ default: '100%', sm: '512px', md: '705px', lg: '100%' }}
                    zIndex="2"
                    overflow="visible"
                    gap="7"
                >
                    <Typography
                        color="white"
                        style={{ fontWeight: 280 }}
                        marginBottom={{ default: 'auto', sm: '0' }}
                        className={styles.title}
                        letterSpacing="-0.06em"
                        as="h1"
                    >
                        <I18n k="logged_out_homepage.video_top_banner.title" />
                    </Typography>
                    {longTermHiringVariant === VARIANTS.A && (
                        <>
                            <Container width="100%" maxWidth="100%">
                                <SearchBar
                                    placeholder={translate(
                                        inExpandableSearchBar
                                            ? 'logged_out_homepage.hero.search_expandable'
                                            : 'logged_out_homepage.hero.search'
                                    )}
                                    source="main_banner"
                                    submitButtonType="button_inside"
                                    theme="dark"
                                    expandable={inExpandableSearchBar}
                                />
                            </Container>
                            <PopularServices />
                        </>
                    )}
                    {longTermHiringVariant === VARIANTS.B && <LongTermHiringVariants variant={VARIANTS.B} />}
                    {longTermHiringVariant === VARIANTS.C && <LongTermHiringVariants variant={VARIANTS.C} />}
                </Stack>
                <Stack marginTop={{ default: '5', sm: 'auto' }} width="100%" alignItems="center">
                    <TrustedBySection />
                    <VideoControlButton videoRef={videoRef} isPlaying={isPlaying} onToggle={handleVideoButtonClick} />
                </Stack>
            </Stack>
        </Stack>
    );
};

export default SafeComponent(VideoHeroBanner);
